import { useMutation, useQueryClient } from '@tanstack/react-query';
import { captureBillingError } from 'services/sentry/captureBillingError';
import {
  SUBSCRIPTION_STATE,
  type GetBillingInfoResponse,
} from '../httpClients/BillingClient/api.schema';
import { BillingClient } from '../httpClients/BillingClient/BillingClient';
import { QUERY_KEYS } from '../queries/consts';
import { MUTATION_KEYS } from './consts';

/**
 * Hook to schedule the current account's self-serve subscription to cancel at
 * the end of the current billing period. The user retains access until then.
 * On success, the cached subscriptionState is updated immediately from the POST
 * response so the UI row switches without waiting for a refetch. On settle, the
 * full billing-info and account-info caches are invalidated to sync cancelAt
 * and any other fields from Stripe.
 */
export const useCancelSubscriptionMutation = () => {
  const queryClient = useQueryClient();
  const billingInfoKey = [QUERY_KEYS.BILLING_INFO];

  return useMutation({
    mutationKey: [MUTATION_KEYS.CANCEL_SUBSCRIPTION],
    mutationFn: BillingClient.cancelSubscription,
    onSuccess: () => {
      queryClient.setQueryData<GetBillingInfoResponse>(
        billingInfoKey,
        (prev) =>
          prev
            ? {
                ...prev,
                subscriptionState: SUBSCRIPTION_STATE.SCHEDULED_TO_CANCEL,
              }
            : prev,
      );
    },
    onError: (error) => {
      captureBillingError(error, 'cancel-subscription');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: billingInfoKey });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.BILLING_ACCOUNT_INFO],
      });
    },
  });
};
